/*
 * Celto cart rules — loaded site-wide from layout/theme.liquid.
 *
 * 1. Quantity lock (Flow 6): every cart line must hold at most 1 unit. The
 *    quantity stepper UI is already removed in the Liquid, but a shopper can
 *    still add the same variant twice from a product page. Whenever the cart
 *    changes we look for any line with quantity > 1 and force it back to 1 via
 *    POST /cart/change.js, then re-announce the change so the bubble / drawer /
 *    cart page refresh.
 *
 * 2. "Tilføj prøvetagningsgebyr" button: when the checkout gate in
 *    snippets/cart-summary.liquid replaces the checkout button, clicking it adds
 *    the sampling fee variant and refreshes the cart (which then qualifies, so
 *    the normal checkout button reappears).
 *
 * The cart UI reacts to the standard `shopify:cart:lines-update` event, which
 * Fabric 4.1.1 reads via direct props on the event object (event.action,
 * event.promise) — see snippets/celto-cart-event-bridge.liquid. We dispatch it
 * the same way, tagging our own events so our listener never reacts to them
 * (guards against an infinite loop).
 */
(function () {
  if (window.__celtoCartRulesInstalled) return;
  window.__celtoCartRulesInstalled = true;

  var FEE_VARIANT_ID = 62541652820317;
  var JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  var enforcing = false;

  // ---------------------------------------------------------------------------
  // Prevent duplicates AT THE SOURCE.
  // A product (variant) may exist in the cart at most once. Instead of only
  // cleaning up after the fact, we intercept every POST to /cart/add(.js) and
  // strip out any variant that is already in the cart (or duplicated inside the
  // same request). This catches every add flow — the bland-selv builder, the
  // tilkøb blocks and normal product forms — regardless of which cart event
  // they fire. Fails open: if anything is unexpected we let the original add
  // through untouched so the cart can never be broken by this guard.
  // ---------------------------------------------------------------------------
  var origFetch = window.fetch.bind(window);

  function variantsInCart(cart) {
    var set = {};
    (cart && cart.items ? cart.items : []).forEach(function (i) {
      set[String(i.variant_id)] = true;
    });
    return set;
  }

  function cartResponse(cart) {
    return new Response(JSON.stringify(cart), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  window.fetch = async function (input, init) {
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method === 'POST' && /\/cart\/add(\.js)?(\?|#|$)/.test(url)) {
        var body = init && init.body;

        // JSON body: { items: [...] } (builder / tilkøb) or { id, quantity } (single).
        if (typeof body === 'string') {
          var parsed = null;
          try { parsed = JSON.parse(body); } catch (e) { parsed = null; }
          if (parsed) {
            var cart = await origFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
            var present = variantsInCart(cart);
            if (Array.isArray(parsed.items)) {
              var seenReq = {};
              var kept = parsed.items.filter(function (it) {
                var id = String(it.id);
                if (present[id] || seenReq[id]) return false; // already in cart / dup in request
                seenReq[id] = true;
                return true;
              });
              if (kept.length === 0) return cartResponse(cart); // nothing new -> no-op
              parsed.items = kept;
              init = Object.assign({}, init, { body: JSON.stringify(parsed) });
            } else if (parsed.id != null && present[String(parsed.id)]) {
              return cartResponse(cart); // single item already present -> block
            }
          }
        } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
          // Standard product forms submit FormData with an `id` field.
          var fid = body.get('id');
          if (fid != null) {
            var cart2 = await origFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
            if (variantsInCart(cart2)[String(fid)]) return cartResponse(cart2); // already present -> block
          }
        }
      }
    } catch (e) {
      /* fail open — never let the guard break a real add */
    }
    return origFetch(input, init);
  };

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  // Build and dispatch a standard cart-lines-update event, tagged as ours so the
  // enforce listener below ignores it.
  function dispatchLinesUpdate(action, itemCount) {
    var evt = new Event('shopify:cart:lines-update', { bubbles: true });
    evt.action = action || 'update';
    evt.context = 'celto-cart-rules';
    evt.celtoRulesSource = true;
    evt.promise = Promise.resolve({
      cart: { totalQuantity: itemCount },
      detail: { itemCount: itemCount, source: 'celto-cart-rules', didError: false }
    });
    document.dispatchEvent(evt);
  }

  async function enforceMaxOne(event) {
    // Never react to the events we dispatch ourselves -> no infinite loop.
    if (event && event.celtoRulesSource) return;
    if (enforcing) return;
    enforcing = true;
    try {
      var cart = await getCart();
      // Each variant may appear AT MOST ONCE, quantity 1. Collapse both cases:
      // (a) a single line with quantity > 1, and (b) the SAME variant spread across
      // several separate lines (happens when the bland-selv builder re-adds a marker
      // that is already in the real cart). Keep the first occurrence, drop the rest.
      var seen = {};
      var changes = []; // { key, quantity } — quantity 0 removes the line
      (cart.items || []).forEach(function (i) {
        if (seen[i.variant_id]) {
          changes.push({ key: i.key, quantity: 0 }); // duplicate line -> remove
        } else {
          seen[i.variant_id] = true;
          if (i.quantity > 1) changes.push({ key: i.key, quantity: 1 }); // cap at 1
        }
      });
      if (changes.length === 0) return; // already clean.

      var lastCart = cart;
      for (var i = 0; i < changes.length; i++) {
        lastCart = await fetch('/cart/change.js', {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ id: changes[i].key, quantity: changes[i].quantity })
        }).then(function (r) {
          return r.json();
        });
      }
      dispatchLinesUpdate('update', lastCart.item_count);
    } catch (e) {
      /* swallow — a failed enforce must not break the cart */
    } finally {
      enforcing = false;
    }
  }

  document.addEventListener('shopify:cart:lines-update', enforceMaxOne);

  // Also sweep once on load, in case the cart already holds >1 of something.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      enforceMaxOne();
    });
  } else {
    enforceMaxOne();
  }

  // "Tilføj prøvetagningsgebyr" — delegated so it survives cart re-renders.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-celto-add-fee]');
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ items: [{ id: FEE_VARIANT_ID, quantity: 1 }] })
    })
      .then(function () {
        return getCart();
      })
      .then(function (cart) {
        dispatchLinesUpdate('add', cart.item_count);
      })
      .catch(function () {
        btn.disabled = false;
      });
  });

  // "Ryd kurv" i kurv-skuffen. Delegeret, saa den overlever at skuffen
  // gen-renderes. POST /cart/clear.js toemmer kurven, og derefter
  // annoncerer vi aendringen praecis som de oevrige regler goer, saa boble,
  // skuffe og kurv-side opdaterer sig selv.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-celto-clear-cart]');
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;

    fetch('/cart/clear.js', { method: 'POST', headers: JSON_HEADERS })
      .then(function () {
        dispatchLinesUpdate('update', 0);
      })
      .catch(function () {
        btn.disabled = false;
      });
  });
})();
